import { useId, useMemo, useState } from "react";

export interface PaletteCommand {
  id: string;
  label: string;
  hint?: string;
  run(): void;
}

interface CommandPaletteProps {
  commands: PaletteCommand[];
  /** Commands built from what the user has typed, shown after the matching built-in ones. */
  commandsForText(text: string): PaletteCommand[];
  onClose(): void;
}

export function matchesPalette(command: PaletteCommand, text: string): boolean {
  const needle = text.trim().toLowerCase();
  return needle === "" || command.label.toLowerCase().includes(needle);
}

export function CommandPalette({ commands, commandsForText, onClose }: CommandPaletteProps) {
  const listId = useId();
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);

  const visible = useMemo(
    () => [...commands.filter((command) => matchesPalette(command, text)), ...commandsForText(text)],
    [commands, commandsForText, text],
  );
  const activeIndex = Math.min(active, Math.max(visible.length - 1, 0));

  const choose = (command: PaletteCommand | undefined) => {
    if (command === undefined) return;
    onClose();
    command.run();
  };

  return (
    <div
      className="overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Command palette">
        <input
          className="palette-input"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={visible[activeIndex] === undefined ? undefined : `${listId}-${activeIndex}`}
          aria-label="Type a command, a filter or a task"
          placeholder="Type a command, a filter, or a task title…"
          autoFocus
          spellCheck={false}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setActive(0);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActive(Math.min(activeIndex + 1, visible.length - 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActive(Math.max(activeIndex - 1, 0));
            } else if (event.key === "Enter") {
              event.preventDefault();
              choose(visible[activeIndex]);
            } else if (event.key === "Escape") {
              event.preventDefault();
              onClose();
            }
          }}
        />
        <ul className="palette-list" id={listId} role="listbox" aria-label="Commands">
          {visible.map((command, position) => (
            <li
              key={command.id}
              id={`${listId}-${position}`}
              role="option"
              aria-selected={position === activeIndex}
            >
              <button
                type="button"
                tabIndex={-1}
                className="palette-item"
                aria-selected={position === activeIndex}
                onMouseMove={() => setActive(position)}
                onClick={() => choose(command)}
              >
                <span>{command.label}</span>
                {command.hint !== undefined && <span className="palette-hint">{command.hint}</span>}
              </button>
            </li>
          ))}
          {visible.length === 0 && <li className="empty">No matching commands</li>}
        </ul>
      </div>
    </div>
  );
}
